import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSessionUser } from '../services/auth';
import { subscribeSession, updateSession } from '../services/session';
import PageLoader from '../components/PageLoader';

const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const [session, setSession] = useState({ loading: true, user: null, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    let changed = false;
    const unsubscribe = subscribeSession(user => {
      changed = true;
      setSession({ loading: false, user, error: null });
    });
    setSession({ loading: true, user: null, error: null });
    getSessionUser().then(user => {
      if (active && !changed) setSession({ loading: false, user, error: null });
    }).catch(error => {
      if (active && !changed) setSession({ loading: false, user: null, error: error.status === 401 ? null : error.message });
    });
    return () => { active = false; unsubscribe(); };
  }, [attempt]);
  useEffect(() => {
    const expiresAt = session.user?.sessionExpiresAt;
    if (!Number.isFinite(expiresAt)) return;
    let timer;
    const checkExpiry = () => {
      clearTimeout(timer);
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) updateSession(null);
      else timer = setTimeout(checkExpiry, Math.min(remaining, 2147483647));
    };
    checkExpiry();
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', checkExpiry);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', checkExpiry);
    };
  }, [session.user]);
  return <AuthContext.Provider value={{ ...session, authenticated: Boolean(session.user), retry: () => setAttempt(value => value + 1) }}>{children}</AuthContext.Provider>;
}
export function useAuth() { return useContext(AuthContext); }
export function AuthStatus() {
  const { error, retry } = useAuth();
  if (!error) return <PageLoader fullPage label="Checking your session..." />;
  return <main style={{ padding: '3rem', fontFamily: 'system-ui' }}>
    {error ? <><p role="alert">{error}</p><button onClick={retry}>Retry</button></> : <p role="status">Checking your session?</p>}
  </main>;
}
